import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, weekImageUrl } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";
import { ArrowLeft, Trash2, ZoomIn, Lock } from "lucide-react";
import { MemberBadge, initials } from "@/components/MemberBadge";

function timeAgo(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export default function WeekDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [week, setWeek] = useState(null);
  const [comments, setComments] = useState([]);
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const loadComments = useCallback(async () => {
    const { data } = await api.get(`/weeks/${id}/comments`);
    setComments(data);
  }, [id]);

  useEffect(() => {
    api
      .get(`/weeks/${id}`)
      .then(({ data }) => setWeek(data))
      .catch(() => setNotFound(true));
    loadComments().catch(() => {});
  }, [id, loadComments]);

  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setBusy(true);
    try {
      await api.post(`/weeks/${id}/comments`, { content });
      setContent("");
      await loadComments();
      toast.success("Your remark has been recorded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (cid) => {
    try {
      await api.delete(`/comments/${cid}`);
      setComments((c) => c.filter((x) => x.id !== cid));
      toast.success("Remark removed.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <p className="font-serif text-4xl text-ink mb-4">Page not found</p>
        <Link to="/archive" className="text-oxblood link-underline pb-0.5 font-mono text-xs tracking-widest uppercase">
          Back to the archive
        </Link>
      </div>
    );
  }

  if (!week) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center font-mono text-xs tracking-widest uppercase text-text-muted animate-pulse">
        Unsealing the page…
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <Link
        to="/archive"
        data-testid="back-to-archive"
        className="inline-flex items-center gap-2 font-mono text-xs tracking-widest uppercase text-text-muted hover:text-oxblood transition-colors mb-10"
      >
        <ArrowLeft strokeWidth={1.5} className="w-4 h-4" /> The Archive
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-12">
        {/* Page image */}
        <div>
          <div className="font-mono text-xs tracking-[0.3em] uppercase text-oxblood mb-3">
            Week {String(week.week_number).padStart(2, "0")}
            {week.page_label ? ` · ${week.page_label}` : ""}
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight leading-none mb-6">
            {week.title}
          </h1>
          {week.description && (
            <p className="font-body text-text-muted leading-relaxed mb-6">{week.description}</p>
          )}
          <button
            onClick={() => setZoom(true)}
            data-testid="page-image-button"
            className="group relative block w-full bg-highlight border border-archive-border offset-shadow overflow-hidden"
          >
            <img
              src={weekImageUrl(week.id)}
              alt={week.title}
              className="w-full max-h-[75vh] object-contain"
            />
            <span className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-ink/85 text-parchment font-mono text-[10px] tracking-widest uppercase px-3 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <ZoomIn strokeWidth={1.5} className="w-3.5 h-3.5" /> Zoom
            </span>
          </button>
        </div>

        {/* Discussion */}
        <div>
          <h2 className="font-serif text-3xl text-ink tracking-tight mb-1">Discussion</h2>
          <div className="font-mono text-xs tracking-widest uppercase text-text-muted mb-8">
            {comments.length} {comments.length === 1 ? "remark" : "remarks"}
          </div>

          <form onSubmit={submit} className="mb-10">
            {user?.has_membership ? (
              <>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={4}
                  data-testid="comment-input"
                  placeholder="Share your reading of this page…"
                  className="w-full bg-surface border border-archive-border px-4 py-3 font-body text-ink resize-none focus:outline-none focus:ring-2 focus:ring-oxblood"
                />
                <button
                  type="submit"
                  disabled={busy || !content.trim()}
                  data-testid="submit-comment-button"
                  className="mt-3 bg-oxblood text-parchment px-6 py-3 font-mono text-xs tracking-widest uppercase hover:bg-oxblood-dark transition-colors disabled:opacity-50"
                >
                  {busy ? "Recording…" : "Post Remark"}
                </button>
              </>
            ) : (
              <div
                data-testid="discussion-paywall"
                className="border border-oxblood bg-oxblood/5 p-6"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Lock strokeWidth={1.5} className="w-4 h-4 text-oxblood" />
                  <span className="font-mono text-xs tracking-widest uppercase text-oxblood">
                    Members only
                  </span>
                </div>
                <p className="font-body text-ink leading-relaxed mb-4">
                  Reading is free, but joining the discussion requires a membership. Become a
                  member to add your voice to this thread.
                </p>
                <Link
                  to="/membership"
                  data-testid="paywall-membership-link"
                  className="inline-block bg-oxblood text-parchment px-6 py-3 font-mono text-xs tracking-widest uppercase hover:bg-oxblood-dark transition-colors"
                >
                  Become a Member
                </Link>
              </div>
            )}
          </form>

          <div className="space-y-6">
            {comments.length === 0 && (
              <p className="font-body italic text-text-muted" data-testid="no-comments">
                No remarks yet. Be the first to respond to this page.
              </p>
            )}
            {comments.map((c) => (
              <div
                key={c.id}
                data-testid={`comment-${c.id}`}
                className="border-b border-archive-border pb-6 last:border-0"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-oxblood text-parchment flex items-center justify-center font-mono text-xs">
                    {initials(c.author_name)}
                  </div>
                  <div className="leading-none">
                    <div className="flex items-center gap-2">
                      <span className="font-body text-ink text-sm">{c.author_name}</span>
                      <MemberBadge role={c.author_role} isMember={c.author_is_member} />
                    </div>
                    <div className="font-mono text-[10px] tracking-widest uppercase text-text-muted mt-1">
                      {timeAgo(c.created_at)}
                    </div>
                  </div>
                  {(user?.id === c.author_id || user?.role === "admin") && (
                    <button
                      onClick={() => remove(c.id)}
                      data-testid={`delete-comment-${c.id}`}
                      className="ml-auto text-text-muted hover:text-oxblood transition-colors"
                      title="Remove remark"
                    >
                      <Trash2 strokeWidth={1.5} className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <p className="font-body text-ink leading-relaxed whitespace-pre-wrap">{c.content}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {zoom && (
        <div
          className="fixed inset-0 z-50 bg-ink/90 flex items-center justify-center p-6"
          onClick={() => setZoom(false)}
          data-testid="zoom-overlay"
        >
          <img
            src={weekImageUrl(week.id)}
            alt={week.title}
            className="max-w-full max-h-full object-contain"
          />
        </div>
      )}
    </div>
  );
}
